import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { kampanyalarData, Kampanya, KampanyaDurumu } from "@/mocks/kampanyalar";
import KampanyaKarti from "./components/KampanyaKarti";
import KampanyaDetayModal from "./components/KampanyaDetayModal";

type StatusFilter = "tumu" | KampanyaDurumu;

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "tumu", label: "Tümü" },
  { key: "Aktif", label: "Aktif" },
  { key: "Taslak", label: "Taslak" },
  { key: "Durduruldu", label: "Durduruldu" },
  { key: "Tamamlandı", label: "Tamamlandı" },
];

export default function KampanyalarPage() {
  const navigate = useNavigate();
  const [kampanyalar, setKampanyalar] = useState<Kampanya[]>(kampanyalarData);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("tumu");
  const [detayKampanya, setDetayKampanya] = useState<Kampanya | null>(null);

  const filtered = statusFilter === "tumu" ? kampanyalar : kampanyalar.filter((k) => k.durum === statusFilter);

  const handleBaslat = (id: number) => {
    setKampanyalar((prev) =>
      prev.map((k) => (k.id === id ? { ...k, durum: "Aktif" as KampanyaDurumu } : k))
    );
  };

  const handleDurdur = (id: number) => {
    setKampanyalar((prev) =>
      prev.map((k) => (k.id === id ? { ...k, durum: "Durduruldu" as KampanyaDurumu } : k))
    );
  };

  const handleKopyala = (id: number) => {
    const orig = kampanyalar.find((k) => k.id === id);
    if (!orig) return;
    const copy: Kampanya = {
      ...orig,
      id: Date.now(),
      ad: orig.ad + " (Kopya)",
      durum: "Taslak",
      gonderilen: 0,
      bekleyen: orig.toplamKisi,
      hatali: 0,
      sonGonderim: "-",
    };
    setKampanyalar((prev) => [copy, ...prev]);
  };

  const handleSil = (id: number) => {
    setKampanyalar((prev) => prev.filter((k) => k.id !== id));
  };

  const stats = {
    toplam: kampanyalar.length,
    aktif: kampanyalar.filter((k) => k.durum === "Aktif").length,
    tamamlanan: kampanyalar.filter((k) => k.durum === "Tamamlandı").length,
    taslak: kampanyalar.filter((k) => k.durum === "Taslak").length,
  };

  return (
    <div className="p-4 md:p-6 xl:p-8 space-y-6 max-w-[1600px]">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">WhatsApp Kampanyaları</h2>
          <p className="text-sm text-gray-500 mt-0.5">Toplu mesaj kampanyalarınızı oluşturun ve yönetin</p>
        </div>
        <button
          onClick={() => navigate("/kampanyalar/yeni")}
          className="px-5 py-2.5 text-sm bg-[#25D366] text-white rounded-xl hover:bg-[#1da851] cursor-pointer whitespace-nowrap flex items-center gap-2 transition-colors font-medium"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line text-base"></i>
          </div>
          Yeni Kampanya Oluştur
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Toplam Kampanya", value: stats.toplam, icon: "ri-megaphone-line", color: "bg-[#0B1120] text-white" },
          { label: "Aktif", value: stats.aktif, icon: "ri-play-circle-line", color: "bg-emerald-500 text-white" },
          { label: "Tamamlanan", value: stats.tamamlanan, icon: "ri-checkbox-circle-line", color: "bg-sky-500 text-white" },
          { label: "Taslak", value: stats.taslak, icon: "ri-draft-line", color: "bg-gray-400 text-white" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.color} flex-shrink-0`}>
              <i className={`${s.icon} text-base`}></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap
              ${statusFilter === f.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}
            `}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Campaign grid */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 py-20 text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto text-gray-300 mb-3">
            <i className="ri-megaphone-line text-4xl"></i>
          </div>
          <p className="text-sm text-gray-500">Bu filtrede kampanya bulunamadı</p>
          <button
            onClick={() => navigate("/kampanyalar/yeni")}
            className="mt-4 px-4 py-2 text-sm bg-[#25D366] text-white rounded-xl hover:bg-[#1da851] cursor-pointer transition-colors"
          >
            Yeni Kampanya Oluştur
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((k) => (
            <KampanyaKarti
              key={k.id}
              kampanya={k}
              onDetay={() => setDetayKampanya(k)}
              onBaslat={() => handleBaslat(k.id)}
              onDurdur={() => handleDurdur(k.id)}
              onKopyala={() => handleKopyala(k.id)}
              onSil={() => handleSil(k.id)}
            />
          ))}
        </div>
      )}

      {/* Detay Modal */}
      {detayKampanya && (
        <KampanyaDetayModal
          kampanya={detayKampanya}
          onClose={() => setDetayKampanya(null)}
        />
      )}
    </div>
  );
}